/**
 * Whether this session sees what a run cost in dollars.
 *
 * The work surface prints a run's sell-side price in five places (the case
 * file's runs list and cost sentence, the attempt fold, the live stream's
 * sub-line, the review case file's total, the run detail page). Those dollars
 * are what a workspace on the metered contract is billed, so a metered
 * customer sees them. A workspace on the pull-request plan
 * (`workspaces.billing_contract = 'pr_plan'`) pays per merged pull request;
 * the rate-card dollars behind a run are YAGNI's cost, not theirs, so they are
 * not shown. A super admin in God Mode sees them on either contract: the cost
 * per merged pull request is how the plan is priced.
 *
 * One reader, so the five surfaces cannot drift: the contract is the only
 * fact that says whether these dollars are the customer's.
 */
import type { AuthSessionResponse, BillingContract } from "@yagni/shared";
import { DEFAULT_BILLING_CONTRACT } from "@yagni/shared";
import { useAuthSession } from "./useWorkspaceRole.js";

/** The contract the session says this workspace bills under; absent reads as metered. */
export function resolveBillingContract(
  session: AuthSessionResponse | null | undefined,
): BillingContract {
  return session?.planFeatures?.billingContract ?? DEFAULT_BILLING_CONTRACT;
}

/**
 * Non-hook form for code that has the session in hand. A missing session (a
 * standalone mount, a test fixture, the shell before the session lands) shows
 * the cost, as every surface did before the contract existed.
 */
export function resolveShowsRunCost(session: AuthSessionResponse | null | undefined): boolean {
  if (session?.godMode?.active === true) return true;
  return resolveBillingContract(session) !== "pr_plan";
}

/** Reads the session the way every surface does (outlet context, else the shell's). */
export function useShowsRunCost(): boolean {
  return resolveShowsRunCost(useAuthSession());
}
